import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import Head from 'next/head';
import Link from 'next/link';
import NextNProgress from 'nextjs-progressbar';
import { NextIntlClientProvider } from 'next-intl';
import { ToastContainer } from 'react-toastify';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'plyr/dist/plyr.css';

import { AscPlatformLayout, AscProvider, AppResponsiveProvider } from '@ascendnow/ascend-components';
import { AscTimeSpentProvider, AuthProvider, PlatformProvider, useLocale } from '@ascendnow/shared';
import { PLATFORM_ROUTES, SIDEBAR_ITEMS, USER_DROPDOWN_OPTIONS } from '@platform/constants';
import '@platform/styles/global.scss';

import ReduxStoreProvider from '../store/StoreProvider';

function App({ Component, pageProps }: AppProps) {
    const { locale, messages } = useLocale();
    const router = useRouter();

    return (
        <NextIntlClientProvider locale={locale} messages={messages}>
            <ReduxStoreProvider>
                <AuthProvider app="PLATFORM" homeRoute={PLATFORM_ROUTES.home} loginRoute={PLATFORM_ROUTES.login}>
                    <Head>
                        <title key="title">The Edge Platform</title>
                        <meta name="viewport" content="width=device-width, initial-scale=1" />
                    </Head>
                    <AppResponsiveProvider>
                        <AscProvider
                            navigateFunction={router.push}
                            linkComponentWrapper={({ href, ...props }) => (
                                <Link legacyBehavior href={href || '#'}>
                                    <a {...props} />
                                </Link>
                            )}
                            theme={'light'}
                        >
                            <NextNProgress
                                color="var(--brandBlue)"
                                height={1}
                                options={{
                                    showSpinner: false,
                                }}
                            />

                            <PlatformProvider>
                                <AscTimeSpentProvider>
                                    <AscPlatformLayout
                                        activePath={router?.pathname}
                                        sidebarItems={SIDEBAR_ITEMS}
                                        userDropdownOptions={USER_DROPDOWN_OPTIONS}
                                        onBackButtonClick={() => router.back()}
                                        globalRankUrl={`${PLATFORM_ROUTES.challenges_ranks_global}?period=allTime`}
                                        classRankUrl={`${PLATFORM_ROUTES.challenges_ranks_school}?period=allTime`}
                                    >
                                        <Component {...pageProps} />
                                    </AscPlatformLayout>
                                </AscTimeSpentProvider>
                            </PlatformProvider>
                        </AscProvider>
                    </AppResponsiveProvider>
                </AuthProvider>
            </ReduxStoreProvider>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </NextIntlClientProvider>
    );
}
export default App;
